import { ref } from 'vue';
import { API_BASE } from './core/native.js';
import { sampleData } from './demo.js';
import { city, cityFirstQuery } from './city.js';
import { askPatiently, busyError } from './busy.js';

// PTV — the network, and the disruption feed.
//
// `fetchDisruptions()` asks THIS APP'S OWN SERVER at /api/disruptions;
// `server/ptv.mjs` holds the developer id and API key, HMAC-signs the request
// PTV requires, caches the answer for ninety seconds and maps it into this
// app's own `disruption` shape.
//
// The credentials are server-side and not negotiable: PTV authenticates with a
// key used to sign every URL, and a key shipped inside a phone binary or a web
// bundle is a key that has been published.
//
// THE FEED IS LIVE, AND THERE IS NO LONGER A FALLBACK. When the server cannot
// answer — unconfigured, unreachable, PTV down — this throws, `refresh()`
// records why, and the ledger keeps the disruptions it already had with their
// age on screen. It used to substitute `sampleFeed` there, which meant an app
// that could not reach anything displayed three invented disruptions as
// current: somebody decides to drive because of a signal fault that never
// happened. The sample survives for `/demo` alone.

/**
 * The metropolitan network. Colours are PTV's official palette — the whole
 * design rests on them being right, and on them living HERE, as data, rather
 * than in a stylesheet.
 *
 * Route ids are PTV `route_id` values. Worth re-checking against a live
 * `/v3/routes?route_types=0` call before the feed is wired up; a wrong id
 * silently matches no disruptions rather than failing loudly.
 */
export const METRO_LINES = [
  { routeId: 1, name: 'Alamein', colour: '#094183' },
  { routeId: 2, name: 'Belgrave', colour: '#094183' },
  { routeId: 7, name: 'Glen Waverley', colour: '#094183' },
  { routeId: 9, name: 'Lilydale', colour: '#094183' },
  { routeId: 3, name: 'Craigieburn', colour: '#ffbe00' },
  { routeId: 15, name: 'Upfield', colour: '#ffbe00' },
  { routeId: 6, name: 'Frankston', colour: '#028430' },
  { routeId: 16, name: 'Werribee', colour: '#028430' },
  { routeId: 17, name: 'Williamstown', colour: '#028430' },
  // THE METRO TUNNEL GROUP, and Sunbury belongs to it now. Before the tunnel
  // opened, Sunbury ran with Craigieburn and Upfield and was yellow with them.
  // It now through-runs with Cranbourne and Pakenham, and PTV's own legend
  // reads "Cranbourne, Pakenham and Sunbury Line" in this blue. The colour is
  // not decoration: a reader at Carnegie watching a train signed SUNBURY needs
  // to see the line they are standing on, and on a drawn map the old yellow
  // put the tunnel's western half in the same colour as the Craigieburn line
  // it crosses at the North Melbourne throat, with no junction there.
  { routeId: 4, name: 'Cranbourne', colour: '#279fd5' },
  { routeId: 11, name: 'Pakenham', colour: '#279fd5' },
  { routeId: 14, name: 'Sunbury', colour: '#279fd5' },
  { routeId: 12, name: 'Sandringham', colour: '#fc7137' },
  { routeId: 5, name: 'Mernda', colour: '#be1014' },
  { routeId: 8, name: 'Hurstbridge', colour: '#be1014' },
  { routeId: 13, name: 'Stony Point', colour: '#909194' },
  { routeId: 1482, name: 'Flemington Racecourse', colour: '#909194' },
];

/**
 * V/Line purple — one colour for all thirteen regional lines.
 *
 * PTV's own map paints every V/Line rail line the same purple, and that is a
 * statement rather than a shortcut: the metropolitan palette encodes which
 * GROUP a line belongs to, while regional services are one network with one
 * identity. Giving thirteen regional lines thirteen colours would invent a
 * grouping PTV does not publish and would exhaust the palette the metro lines
 * depend on for meaning.
 */
export const VLINE_COLOUR = '#8f1a95';

/**
 * The V/Line regional rail lines. PTV `route_type` 3, rail only.
 *
 * THIRTEEN, and only thirteen. PTV files V/Line's long-distance COACHES under
 * the same `route_type` 3 — forty-six of them, "Mt Buller-Mansfield - Melbourne
 * via Yea", "Adelaide - Melbourne via Nhill & Bendigo" — and several of those
 * coach routes list more railway stations than the Geelong line does, because a
 * coach service is timetabled as train-then-coach and carries the train leg's
 * stations in its stop list. So the stops CANNOT tell a rail line from a coach,
 * and `scripts/fetch-network.mjs` does not try: see the note there on the
 * discriminator it uses (PTV's `route_gtfs_id`) and what corroborates it.
 *
 * ALPHABETICAL, which is how PTV lists them. The metropolitan order above is
 * the network map's colour grouping, and there is no equivalent here: every
 * regional line is the same purple, so a corridor grouping would carry no
 * visual meaning and would only be one more thing to keep in step with a map.
 *
 * Route ids are PTV's own and are verified live — none of them falls in the
 * 1001+/2001+/3001+/4001+ ranges the other four cities' synthetic ids occupy.
 */
export const REGIONAL_LINES = [
  { routeId: 1706, name: 'Albury', colour: VLINE_COLOUR, regional: true },
  { routeId: 1837, name: 'Ararat', colour: VLINE_COLOUR, regional: true },
  { routeId: 1823, name: 'Bairnsdale', colour: VLINE_COLOUR, regional: true },
  { routeId: 1728, name: 'Ballarat', colour: VLINE_COLOUR, regional: true },
  { routeId: 1740, name: 'Bendigo', colour: VLINE_COLOUR, regional: true },
  { routeId: 1849, name: 'Echuca', colour: VLINE_COLOUR, regional: true },
  { routeId: 1745, name: 'Geelong', colour: VLINE_COLOUR, regional: true },
  { routeId: 4871, name: 'Maryborough', colour: VLINE_COLOUR, regional: true },
  { routeId: 1710, name: 'Seymour', colour: VLINE_COLOUR, regional: true },
  { routeId: 1908, name: 'Shepparton', colour: VLINE_COLOUR, regional: true },
  { routeId: 1848, name: 'Swan Hill', colour: VLINE_COLOUR, regional: true },
  { routeId: 1824, name: 'Traralgon', colour: VLINE_COLOUR, regional: true },
  { routeId: 1853, name: 'Warrnambool', colour: VLINE_COLOUR, regional: true },
];

/**
 * Melbourne's lines, metropolitan first and regional below — the order every
 * screen that lists the whole network reads them in.
 *
 * ONE LIST, because a V/Line line is a line: it has a name, a colour, a stop
 * list, disruptions, a board and a subscription, and every screen that already
 * handles seventeen of them handles thirty. Where the two KINDS have to be
 * told apart — the Lines tab's REGIONAL heading, the trip planner's refusal to
 * route onto a reserved-seat service — the `regional` flag is what says so, and
 * it is a property of the line rather than a range of route ids somebody has to
 * remember.
 */
export const MELBOURNE_LINES = [...METRO_LINES, ...REGIONAL_LINES];

/** The route ids that are V/Line rather than Metro. */
export const REGIONAL_ROUTE_IDS = new Set(REGIONAL_LINES.map((l) => l.routeId));

/** Is this one of Melbourne's regional lines? False for every other city's ids. */
export function isRegionalRoute(routeId) {
  return REGIONAL_ROUTE_IDS.has(Number(routeId));
}

/** Where a tapped disruption sends you when it has no url of its own. */
export function disruptionsUrl() {
  return city().disruptionsUrl;
}

// A minute, in ms — the unit every relative time in this file is written in.
const MIN = 60_000;

/**
 * Sample data, for `/demo?fixture=sample` and the QA runs. Unreachable in the
 * real app, and unreachable on a plain `/demo` — which reads the live feed.
 *
 * Times are relative to the call so the screens read as live rather than as a
 * fixture frozen in 2026. `delayMinutes` is NOT a PTV field — disruptions data
 * gives words, not figures — so `statusFor` reads the figure out of the prose
 * instead, exactly as it does with the real feed.
 */
function sampleFeed(now) {
  return [
    {
      ptvId: 302511,
      routeId: 11,
      title: 'Major delays, Pakenham line',
      // PTV's own `disruption_type`, as the live feed sends it. `severityOf`
      // reads this before it reads the prose, so a sample row without one
      // classifies differently from the real thing it stands in for.
      type: 'Major Delays',
      description:
        'Buses are replacing trains between Caulfield and Dandenong following a signal fault at Caulfield. '
        + 'Trains are running to an altered timetable and delays of up to 20 minutes are expected in both directions. '
        + 'Allow extra travel time, or consider the Cranbourne line to Dandenong.',
      status: 'current',
      startedAt: new Date(now - 47 * MIN).toISOString(),
      url: 'https://www.ptv.vic.gov.au/live-travel-updates/',
    },
    {
      ptvId: 302488,
      routeId: 12,
      title: 'Buses replace trains, Sandringham line',
      type: 'Planned Works',
      description:
        'Buses replace trains between Elsternwick and Sandringham from 8:30pm Friday to the last service Sunday '
        + 'for scheduled maintenance works. Buses depart from the Nepean Highway side of Elsternwick station.',
      status: 'planned',
      startedAt: new Date(now + 26 * 60 * MIN).toISOString(),
      url: 'https://www.ptv.vic.gov.au/live-travel-updates/',
    },
    {
      ptvId: 302470,
      routeId: 5,
      title: 'Minor delays, Mernda line',
      type: 'Minor Delays',
      description:
        'Trains are running up to 8 minutes late after an earlier equipment fault near Reservoir. '
        + 'Services are recovering and no replacement buses are in operation.',
      status: 'current',
      startedAt: new Date(now - 3 * 60 * MIN).toISOString(),
      url: 'https://www.ptv.vic.gov.au/live-travel-updates/',
    },
  ];
}

/**
 * True when the last poll came from sample data rather than PTV.
 *
 * Only ever true where the sample is in force. `refresh()` reads it to decide
 * whether the feed is authoritative enough to CLEAR stored disruptions — the
 * sample must never retire a row a live feed put on the ledger. A live `/demo`
 * poll is a live feed and reconciles like any other.
 */
export const usingSampleData = ref(sampleData.value);

/** What every screen says when there is no answer to be had. One voice. */
const unreachable = () => `Couldn’t reach ${city().sourceShort}.`;

/**
 * One poll.
 *
 * Asks THIS APP'S OWN SERVER, which holds the PTV credentials and signs the
 * request. The key never reaches the client: a key in a phone binary or a web
 * bundle is a published key.
 *
 * Throws on anything that is not a live answer. `configured: false` counts:
 * a server with no credentials knows nothing about the trains, and an app that
 * showed invented ones instead would be wrong in the most expensive direction.
 */
export async function fetchDisruptions({ now = Date.now() } = {}) {
  if (sampleData.value) {
    usingSampleData.value = true;
    return sampleFeed(now);
  }

  // ONE ATTEMPT, AND ONE RETRY IF THE GATE SAID BUSY. The disruption feed asks
  // at ALERT priority and is the last thing the budget ever refuses, but it
  // can be refused — and a refusal read as "couldn't reach PTV" is this app
  // telling somebody the disruption feed is down when it is ninety seconds old
  // and fine. See `src/busy.js`.
  const answer = await askPatiently(async () => {
    try {
      // The only endpoint with no query string of its own, so it takes the `?`
      // form. Melbourne sends none at all and the URL is the one it has always
      // been — which matters, because this response is cached by URL.
      const res = await fetch(`${API_BASE}/api/disruptions${cityFirstQuery()}`, { signal: AbortSignal.timeout(12_000) });
      const parsed = await res.json().catch(() => null);
      return { ok: res.ok, body: parsed, status: res.status };
    } catch {
      // No server, no signal, or twelve seconds gone. Same answer either way.
      return { ok: false, body: null, status: 0 };
    }
  });

  if (answer.ok && answer.body?.configured && Array.isArray(answer.body.disruptions)) {
    usingSampleData.value = false;
    return answer.body.disruptions;
  }

  throw answer.busy ? busyError(answer) : new Error(unreachable());
}
